import React, { useState, useEffect } from 'react'
import QRCode from 'qrcode'

export const MFAModal: React.FC<{
  isOpen: boolean;
  availableMethods?: string[];
  setupMethods?: string[];
  onVerify: (code: string, rememberDevice?: boolean) => void;
  onUseRecovery: (code: string, rememberDevice?: boolean) => void;
  onVerifyPasskey?: (rememberDevice?: boolean) => void;
  onClose: () => void;
  error?: string;
  requiresSetup?: boolean;
  onSetup?: (code: string) => void;
  onSetupPasskey?: () => void;
  otpauth?: string;
  rememberDays?: number;
}> = ({ isOpen, availableMethods = ['totp'], setupMethods = ['totp', 'passkey'], onVerify, onUseRecovery, onVerifyPasskey, onClose, error, requiresSetup, onSetup, onSetupPasskey, otpauth, rememberDays = 14 }) => {
  const [code, setCode] = React.useState('')
  const [recovery, setRecovery] = React.useState('')
  const [rememberDevice, setRememberDevice] = React.useState(true)
  const [mode, setMode] = React.useState<'totp' | 'passkey'>('totp')
  const [qrDataUrl, setQrDataUrl] = useState<string>('')

  useEffect(() => {
    if (!otpauth) {
      setQrDataUrl('')
      return
    }
    QRCode.toDataURL(otpauth, { width: 200, margin: 2 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''))
  }, [otpauth])

  React.useEffect(() => {
    if (!requiresSetup) {
      if (availableMethods.includes('passkey') && !availableMethods.includes('totp')) setMode('passkey')
      else setMode('totp')
    } else {
      setMode('totp')
    }
  }, [availableMethods, requiresSetup])
  if (!isOpen) return null
  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg">Multi‑Factor Authentication</h3>
        {requiresSetup ? (
          <>
            <p className="py-2">Set up at least one MFA method to continue.</p>
            <div className="flex gap-2 mb-3 flex-wrap">
              {setupMethods.includes('passkey') && (
                <button className="btn btn-outline" onClick={() => onSetupPasskey?.()}>Set Up Passkey</button>
              )}
              {setupMethods.includes('totp') && (
                <button className={`btn ${mode === 'totp' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setMode('totp')}>Set Up Authenticator App</button>
              )}
            </div>
            {mode === 'totp' && otpauth && (
              <>
                {qrDataUrl ? <img alt="TOTP QR code" src={qrDataUrl} width={200} height={200} /> : <div className="w-[200px] h-[200px] bg-base-300 animate-pulse rounded" aria-hidden />}
                <div className="mt-2">
                  <label className="block text-sm mb-1">Or copy URI for a desktop authenticator/password manager:</label>
                  <div className="join w-full">
                    <input className="input input-bordered join-item w-full" readOnly value={otpauth} />
                    <button className="btn join-item" onClick={() => navigator.clipboard.writeText(otpauth)}>Copy</button>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <p className="py-2">Choose an MFA method to continue.</p>
            <div className="flex gap-2 mb-3 flex-wrap">
              {availableMethods.includes('totp') && (
                <button className={`btn ${mode === 'totp' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setMode('totp')}>Authenticator Code</button>
              )}
              {availableMethods.includes('passkey') && (
                <button className={`btn ${mode === 'passkey' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setMode('passkey')}>Use Passkey</button>
              )}
            </div>
          </>
        )}
        {error && <div className="alert alert-error my-2">{error}</div>}
        <div className="space-y-3">
          {(!requiresSetup && mode === 'passkey') && (
            <button className="btn btn-primary w-full" onClick={() => onVerifyPasskey?.(rememberDevice)}>Continue with Passkey</button>
          )}
          {(requiresSetup || mode === 'totp') && (
            <input className="input input-bordered w-full" placeholder="Authenticator code" value={code} onChange={e => setCode(e.target.value)} maxLength={8} />
          )}
          {!requiresSetup && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="checkbox checkbox-sm" checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} />
              <span className="text-sm">Remember this device for {rememberDays} days</span>
            </label>
          )}
          {(requiresSetup || mode === 'totp') && (
            <button className="btn btn-primary w-full" onClick={() => (requiresSetup && onSetup ? onSetup(code) : onVerify(code, rememberDevice))}>{requiresSetup ? 'Verify & Enable' : 'Verify'}</button>
          )}
          {!requiresSetup && (
            <>
              <div className="divider">OR</div>
              <input className="input input-bordered w-full" placeholder="Recovery code (XXXX-XXXX-XXXX)" value={recovery} onChange={e => setRecovery(e.target.value)} />
              <button className="btn w-full" onClick={() => onUseRecovery(recovery, rememberDevice)}>Use Recovery Code</button>
            </>
          )}
        </div>
        <div className="modal-action">
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

export default MFAModal


